import React from 'react';
import { Scissors, Zap, Flame, Droplets, Wind, Coffee, SmilePlus, HeartHandshake, Star, Award } from 'lucide-react';
import { services } from '../data/mockData';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';

const iconMap = {
  Scissors: Scissors,
  Zap: Zap,
  Flame: Flame,
  Droplets: Droplets,
  Wind: Wind,
  Coffee: Coffee,
  SmilePlus: SmilePlus,
  HeartHandshake: HeartHandshake,
};

const ServicesSection = () => {
  return (
    <section id="services" className="py-20 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Experience Badge & Google Rating */}
        <AnimateOnScroll>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-12">
            <div className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white/5 border border-primary/20">
              <Award className="w-6 h-6 text-primary" />
              <span className="text-base sm:text-lg font-bold text-primary uppercase tracking-wider">
                10+ Years of Experience
              </span>
            </div>
            <div className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white/5 border border-primary/20">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-5 h-5 text-primary fill-current" />
                ))}
                <Star className="w-5 h-5 text-primary fill-current" style={{ clipPath: 'inset(0 20% 0 0)' }} />
              </div>
              <span className="text-base sm:text-lg font-bold text-white">
                4.8
              </span>
              <span className="text-sm text-zinc-400 font-medium">
                on Google
              </span>
            </div>
          </div>
        </AnimateOnScroll>

        {/* Why Golden Scissors - Professional Section */}
        <AnimateOnScroll>
          <div className="mb-20 md:mb-28">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              {/* Image */}
              <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/3] group w-full">
                <div className="absolute inset-0 bg-primary/10 z-10 mix-blend-overlay group-hover:opacity-0 transition-opacity duration-700" />
                <img
                  src="https://customer-assets.emergentagent.com/job_scissors-shop-1/artifacts/q4o67xdo_12.png"
                  alt="Professional barber at Golden Scissors"
                  className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
              </div>
              {/* Text */}
              <div className="text-center lg:text-left">
                <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold uppercase tracking-tight mb-6 italic leading-[1.1]">
                  Market Harborough's <span className="gold-gradient">Finest</span>
                </h2>
                <p className="text-zinc-400 text-base md:text-lg leading-relaxed mb-5">
                  Golden Scissors has proudly served the people of Market Harborough for over a decade, earning a reputation as the town's most trusted barbershop. Our master barbers combine traditional craftsmanship with modern techniques to deliver cuts that turn heads.
                </p>
                <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
                  From classic fades to contemporary styles, every client receives a personalised consultation and premium experience. It's why locals keep coming back — and why we're consistently rated the top barbershop in Harborough.
                </p>
              </div>
            </div>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll>
          <div className="text-center mb-16 md:mb-24">
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold uppercase tracking-tight mb-4 italic">
              Our <span className="gold-gradient">Services</span>
            </h2>
            <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">
              Precision engineering for your head. We don&apos;t just cut hair, we
              design confidence.
            </p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <AnimateOnScroll key={service.title} delay={index * 100}>
                <div className="group bg-card border border-white/5 rounded-3xl p-8 hover:border-primary/40 hover:bg-[#151518] transition-all duration-500 hover:-translate-y-2 h-full">
                  <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6 text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-all duration-500">
                    {IconComponent && <IconComponent className="w-6 h-6" />}
                  </div>
                  <h3 className="font-heading text-2xl text-white font-bold mb-3 uppercase italic">
                    {service.title}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
